import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { describeError, useToast } from "../components/toast";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import type { GlobalSchemeDefault, SchemeKey } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { errorText } from "../utils/apiError";
import ColorSwatch from "../components/ColorSwatch";
import SchemeThumbnail from "../components/template/SchemeThumbnail";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { EditDrawer } from "../components/EditDrawer";

interface Edit {
  name: string;
  palette: Record<string, string>;
}

/** Build the editable state for a template: stored global override, falling
 *  back to the code-authored manifest defaults. */
function initialEdit(key: SchemeKey, stored?: GlobalSchemeDefault): Edit {
  const m = SCHEME_MANIFESTS[key];
  const palDefaults = m.defaultTokens.palette as Record<string, string>;
  const palette: Record<string, string> = {};
  for (const { key: pk } of m.tokenSchema?.palette ?? []) {
    palette[pk] = stored?.palette?.[pk] ?? palDefaults[pk] ?? "";
  }
  return { name: stored?.name ?? m.name, palette };
}

/**
 * Global Templates - admin edits each template's default colours + name once.
 * Those defaults seed a premise's binding when it first picks the template
 * (existing premises keep their own values). The manifest stays the code-level
 * fallback; what's stored here is a diff over it.
 */
/**
 * One template's defaults form, in the right-hand drawer. Holds its own draft;
 * the page saves it and resolves true so the drawer slides out first.
 */
function TemplateDefaultsDrawer({
  schemeKey,
  initial,
  saving,
  onClose,
  onSave,
}: {
  schemeKey: SchemeKey;
  initial: Edit;
  saving: boolean;
  onClose: () => void;
  onSave: (edit: Edit) => Promise<boolean>;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const m = SCHEME_MANIFESTS[schemeKey];
  const paletteKeys = m.tokenSchema?.palette ?? [];
  const [e, setE] = useState<Edit>(initial);
  const setColor = (pk: string, v: string) =>
    setE((cur) => ({ ...cur, palette: { ...cur.palette, [pk]: v } }));

  return (
    <EditDrawer
      open
      onClose={onClose}
      busy={saving}
      title={t("globalTemplatesPage.drawerTitle", { name: m.name })}
      subtitle={t("globalTemplatesPage.drawerSubtitle")}
      data-testid={`template-defaults-drawer-${schemeKey}`}
      actions={(dismiss) => (
        <>
          <Button color="inherit" disabled={saving} onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={async () => {
              if (await onSave(e)) dismiss();
            }}
            data-testid={`save-template-${schemeKey}`}
          >
            {saving
              ? t("common:states.saving")
              : t("globalTemplatesPage.saveDefaults")}
          </Button>
        </>
      )}
    >
      <Box sx={{ mb: 2 }}>
        <SchemeThumbnail schemeKey={schemeKey} />
      </Box>
      <TextField
        size="small"
        fullWidth
        label={t("globalTemplatesPage.displayName")}
        value={e.name}
        onChange={(ev) => setE((cur) => ({ ...cur, name: ev.target.value }))}
        sx={{ mb: 2 }}
      />

      <Typography variant="subtitle2" gutterBottom>
        {t("globalTemplatesPage.colours")}
      </Typography>
      <Stack spacing={1}>
        {paletteKeys.map(({ key: pk, label }) => (
          <Stack key={pk} direction="row" spacing={1.5} alignItems="center">
            <ColorSwatch
              value={e.palette[pk] ?? ""}
              onChange={(v) => setColor(pk, v)}
              ariaLabel={label}
            />
            <TextField
              size="small"
              label={label}
              value={e.palette[pk] ?? ""}
              onChange={(ev) => setColor(pk, ev.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>
        ))}
      </Stack>
    </EditDrawer>
  );
}

export default function GlobalTemplatesPage() {
  /** The defaults as last loaded or saved; the drawer edits a copy. */
  const [edits, setEdits] = useState<Record<string, Edit> | null>(null);
  const [editingKey, setEditingKey] = useState<SchemeKey | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["templates", "common"]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    foyerApi
      .listSchemeDefaults()
      .then((list) => {
        const stored: Record<string, GlobalSchemeDefault> = {};
        for (const d of list) stored[d.key] = d;
        const init: Record<string, Edit> = {};
        for (const m of Object.values(SCHEME_MANIFESTS)) {
          init[m.key] = initialEdit(m.key, stored[m.key]);
        }
        setEdits(init);
      })
      .catch((e) => setError(errorText(e, t)));
  }, [t]);

  /** Resolves true on success, so the drawer can slide out first. */
  async function save(key: SchemeKey, e: Edit): Promise<boolean> {
    const m = SCHEME_MANIFESTS[key];
    const palDefaults = m.defaultTokens.palette as Record<string, string>;
    // Store only what differs from the manifest - keep defaults sparse.
    const paletteDiff: Record<string, string> = {};
    for (const { key: pk } of m.tokenSchema?.palette ?? []) {
      const v = e.palette[pk]?.trim();
      if (v && v !== palDefaults[pk]) paletteDiff[pk] = v;
    }
    const name =
      e.name.trim() && e.name.trim() !== m.name ? e.name.trim() : null;
    setSavingKey(key);
    try {
      await foyerApi.saveSchemeDefault(key, {
        name,
        palette: Object.keys(paletteDiff).length ? paletteDiff : null,
      });
      setEdits((cur) => (cur ? { ...cur, [key]: e } : cur));
      toast.success(
        t("globalTemplatesPage.saved", { name: e.name.trim() || m.name })
      );
      return true;
    } catch (err) {
      toast.error(
        describeError(t, "saveTemplate", err, { name: e.name.trim() || m.name })
      );
      return false;
    } finally {
      setSavingKey(null);
    }
  }

  if (error && !edits) {
    return (
      <PageContainer>
        <Alert severity="error">{error}</Alert>
      </PageContainer>
    );
  }
  if (!edits) {
    return (
      <PageContainer>
        <PageHeader
          eyebrow={t("globalTemplatesPage.eyebrow")}
          title={t("globalTemplatesPage.title")}
        />
        <TableSkeleton rows={4} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("globalTemplatesPage.eyebrow")}
        title={t("globalTemplatesPage.title")}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        {Object.values(SCHEME_MANIFESTS).map((m) => {
          const e = edits[m.key];
          const paletteKeys = m.tokenSchema?.palette ?? [];
          return (
            <Card key={m.key} variant="outlined">
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <SchemeThumbnail schemeKey={m.key} />
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "fontWeightSemibold" }}
                  >
                    {m.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      m.segment === "care"
                        ? t("common:labels.care")
                        : t("globalTemplatesPage.hotel")
                    }
                    variant="outlined"
                  />
                </Stack>

                {e.name.trim() && e.name.trim() !== m.name && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    {t("globalTemplatesPage.shownAs", { name: e.name.trim() })}
                  </Typography>
                )}

                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ flexWrap: "wrap", rowGap: 0.75 }}
                  data-testid={`template-colours-${m.key}`}
                >
                  {paletteKeys.map(({ key: pk, label }) => (
                    <Box
                      key={pk}
                      title={t("globalTemplatesPage.swatch", {
                        label,
                        value: e.palette[pk] || "-",
                      })}
                      aria-label={t("globalTemplatesPage.swatch", {
                        label,
                        value: e.palette[pk] || t("globalTemplatesPage.notSet"),
                      })}
                      role="img"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "6px",
                        border: "1px solid rgba(0,0,0,0.12)",
                        bgcolor: e.palette[pk] || "transparent",
                      }}
                    />
                  ))}
                </Stack>

                <Box sx={{ mt: 2, textAlign: "right" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setEditingKey(m.key)}
                    data-testid={`edit-template-${m.key}`}
                  >
                    {t("common:buttons.edit")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {editingKey && (
        <TemplateDefaultsDrawer
          key={editingKey}
          schemeKey={editingKey}
          initial={edits[editingKey]}
          saving={savingKey === editingKey}
          onClose={() => setEditingKey(null)}
          onSave={(e) => save(editingKey, e)}
        />
      )}
    </PageContainer>
  );
}
